// Referral Tiers
// Bronze (1-5): 7 dias premium
// Prata (6-15): 10 dias premium + Badge
// Ouro (16-30): 14 dias + R$5 credito + Beta access
// Embaixador (31+): R$10 credito + 50% off vitalicio + Suporte prioritario

export type ReferralTier = 'bronze' | 'prata' | 'ouro' | 'embaixador';

export type ReferralStatus = 'pending' | 'validated' | 'rejected';

export type ReferralRewardType = 'premium_days' | 'credits' | 'both';

// Referral entity
export interface Referral {
  id: string;
  referrerId: string;
  refereeId: string;
  referrerPhone?: string;
  referrerName?: string;
  refereePhone?: string;
  refereeName?: string;
  status: ReferralStatus;
  rewardType?: ReferralRewardType;
  premiumDaysAwarded?: number;
  creditsAwarded?: number;
  rejectionReason?: string;
  createdAt: string;
  validatedAt?: string;
  rejectedAt?: string;
}

// User with referral stats
export interface ReferralUser {
  id: string;
  userId: string;
  referralCode: string;
  currentTier: ReferralTier;
  totalReferrals: number;
  validatedReferrals: number;
  pendingReferrals: number;
  totalPremiumDaysEarned: number;
  totalCreditsEarned: string;
  availableCredits: string;
  referredBy: string | null;
  lastReferralAt: string | null;
  tierUpdatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    name?: string;
    phoneNumber?: string;
    email?: string | null;
  };
  // Legacy fields for backwards compatibility
  tier?: ReferralTier;
  name?: string;
  phoneNumber?: string;
  credits?: number;
  hasBadge?: boolean;
  hasBetaAccess?: boolean;
  hasLifetimeDiscount?: boolean;
  hasPrioritySupport?: boolean;
}

// Reward record
export interface ReferralReward {
  id: string;
  userId: string;
  referralId: string;
  type: ReferralRewardType;
  premiumDays?: number;
  creditAmount?: number;
  status: 'pending' | 'applied' | 'expired';
  createdAt: string;
  appliedAt?: string;
}

// Global stats - matches API response
export interface ReferralStats {
  // API fields
  success?: boolean;
  totalReferrals?: number;
  pendingReferrals?: number;
  validatedReferrals?: number;
  expiredReferrals?: number;
  rejectedReferrals?: number;
  totalPremiumDaysGiven?: number;
  totalCreditsGiven?: number;
  tierDistribution?: {
    bronze?: number;
    silver?: number;
    gold?: number;
    ambassador?: number;
  };
  topReferrers?: Array<{
    userId?: string;
    name?: string;
    phoneNumber?: string;
    validated?: number;
    tier?: string;
    credits?: number;
  }>;
}

// Detail response
export interface ReferralDetail {
  referral: Referral;
  referrer: ReferralUser;
  referee: ReferralUser;
}

// User detail response
export interface ReferralUserDetail {
  user: ReferralUser;
  referrals: Referral[];
  rewards: ReferralReward[];
}

// List response
export interface ReferralListResponse {
  referrals: Referral[];
  total: number;
  page: number;
  limit: number;
}

export interface ReferralUserListResponse {
  users: ReferralUser[];
  total: number;
  page: number;
  limit: number;
}

// Filters
export interface ReferralFilters {
  status?: ReferralStatus | 'all';
  referrerId?: string;
  page?: number;
  limit?: number;
}

export interface ReferralUserFilters {
  search?: string;
  tier?: ReferralTier | 'all';
  page?: number;
  limit?: number;
}

// Inputs
export interface AdjustCreditsInput {
  amount: number;
  reason: string;
}

export interface ChangeTierInput {
  tier: ReferralTier;
}

export interface RejectReferralInput {
  reason: string;
}

// Tier configuration (for UI)
export const TIER_CONFIG: Record<ReferralTier, {
  label: string;
  minReferrals: number;
  maxReferrals: number | null;
  premiumDays: number;
  credits: number;
  color: string;
  bgColor: string;
  benefits: string[];
}> = {
  bronze: {
    label: 'Bronze',
    minReferrals: 1,
    maxReferrals: 5,
    premiumDays: 7,
    credits: 0,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    benefits: ['7 dias premium por indicacao'],
  },
  prata: {
    label: 'Prata',
    minReferrals: 6,
    maxReferrals: 15,
    premiumDays: 10,
    credits: 0,
    color: 'text-slate-500 dark:text-slate-400',
    bgColor: 'bg-slate-100 dark:bg-slate-800',
    benefits: ['10 dias premium por indicacao', 'Badge no perfil'],
  },
  ouro: {
    label: 'Ouro',
    minReferrals: 16,
    maxReferrals: 30,
    premiumDays: 14,
    credits: 5,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30',
    benefits: ['14 dias premium por indicacao', 'R$5 credito por indicacao', 'Acesso beta features'],
  },
  embaixador: {
    label: 'Embaixador',
    minReferrals: 31,
    maxReferrals: null,
    premiumDays: 0,
    credits: 10,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    benefits: ['R$10 credito por indicacao', '50% off vitalicio', 'Suporte prioritario'],
  },
};

// Status configuration (for UI)
export const STATUS_CONFIG: Record<ReferralStatus, {
  label: string;
  color: string;
  bgColor: string;
}> = {
  pending: {
    label: 'Pendente',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
  },
  validated: {
    label: 'Validada',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  rejected: {
    label: 'Rejeitada',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
  },
};

// Helper function to get tier from referral count
export function getTierFromReferrals(count: number): ReferralTier {
  if (count >= 31) return 'embaixador';
  if (count >= 16) return 'ouro';
  if (count >= 6) return 'prata';
  return 'bronze';
}
